import React, { useState, useEffect, useCallback } from 'react';
import api, { getApiErrorMessage } from '../../../services/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';
import { Label } from '../../../components/ui/label';
import { LoadingSpinner } from '../../../components/layout/SharedComponents';
import { Brain, FileText, Download, ChevronDown } from 'lucide-react';
import { SortableTableHeader, useSortState, sortData } from '../../../components/SortableTableHeader';

export default function AIBillingPanel() {
  const [settings, setSettings] = useState({ ai_carbon_enabled: false, ai_ocr_enabled: false });
  const [report, setReport] = useState(null);
  const [featSort, setFeatSort] = useState({ field: '', dir: 'asc' });
  const [userSort, setUserSort] = useState({ field: '', dir: 'asc' });
  const [monthSort, setMonthSort] = useState({ field: '', dir: 'asc' });
  const [logSort, setLogSort] = useState({ field: '', dir: 'asc' });
  const hFeat = (f, d) => setFeatSort({ field: f, dir: d });
  const hUser = (f, d) => setUserSort({ field: f, dir: d });
  const hMonth = (f, d) => setMonthSort({ field: f, dir: d });
  const hLog = (f, d) => setLogSort({ field: f, dir: d });
  const [quarter, setQuarter] = useState('');
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState({ data: [], total: 0, summary: {} });

  const loadSettings = useCallback(async () => {
    try {
      const r = await api.get('/api/ai/settings');
      setSettings(r.data);
    } catch {}
  }, []);

  const loadQuarterlyReport = useCallback(async (q) => {
    try {
      const r = await api.get('/api/ai/usage/quarterly-report', { params: { quarter: q || '' } });
      setReport(r.data);
      if (!q) setQuarter(r.data.quarter);
    } catch {}
  }, []);

  const loadUsage = useCallback(async () => {
    try {
      const r = await api.get('/api/ai/usage', { params: { page_size: 20, quarter } });
      setUsage(r.data);
    } catch {}
  }, [quarter]);

  useEffect(() => {
    Promise.all([loadSettings(), loadQuarterlyReport(''), loadUsage()]).finally(() => setLoading(false));
  }, []); // eslint-disable-line

  useEffect(() => { if (quarter) { loadQuarterlyReport(quarter); loadUsage(); } }, [quarter]); // eslint-disable-line

  const toggleSetting = async (key) => {
    const newVal = !settings[key];
    try {
      await api.put('/api/ai/settings', { [key]: newVal });
      setSettings(p => ({ ...p, [key]: newVal }));
      toast.success(`${key === 'ai_carbon_enabled' ? 'Carbon estimation' : 'OCR'} ${newVal ? 'enabled' : 'disabled'}`);
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to update')); }
  };

  const quarters = [];
  const now = new Date();
  for (let i = 0; i < 8; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i * 3, 1);
    const q = Math.floor(d.getMonth() / 3) + 1;
    quarters.push(`${d.getFullYear()}-Q${q}`);
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6" data-testid="ai-billing-panel">
      {/* Feature Toggles */}
      <div className="border rounded-xl p-5">
        <h3 className="text-sm font-bold mb-4 flex items-center gap-2">
          <Brain className="h-4 w-4 text-primary" /> AI Feature Settings
        </h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Carbon Emissions Estimation</p>
              <p className="text-xs text-muted-foreground">Automatically estimate CO2e for line items using AI when adding items to requisitions</p>
            </div>
            <Switch checked={settings.ai_carbon_enabled} onCheckedChange={() => toggleSetting('ai_carbon_enabled')} data-testid="toggle-carbon" />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Invoice OCR Processing</p>
              <p className="text-xs text-muted-foreground">Extract data from uploaded invoices using AI-powered OCR (coming soon)</p>
            </div>
            <Switch checked={settings.ai_ocr_enabled} onCheckedChange={() => toggleSetting('ai_ocr_enabled')} data-testid="toggle-ocr" />
          </div>
        </div>
        <div className="mt-4 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
          AI features incur per-call costs. These costs are tracked and included in the quarterly audit report below.
        </div>
      </div>

      {/* Quarterly Report */}
      <div className="border rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" /> Quarterly AI Usage Report
          </h3>
          <div className="flex items-center gap-2">
            <select value={quarter} onChange={e => setQuarter(e.target.value)} className="h-8 rounded-lg border border-input bg-background px-2 text-sm">
              {quarters.map(q => <option key={q} value={q}>{q}</option>)}
            </select>
          </div>
        </div>

        {report && (
          <div className="space-y-5">
            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-3">
              <div className="border rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-primary">{report.totals?.total_calls || 0}</p>
                <p className="text-[11px] text-muted-foreground">Total API Calls</p>
              </div>
              <div className="border rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-primary">${(report.totals?.total_cost || 0).toFixed(4)}</p>
                <p className="text-[11px] text-muted-foreground">Total Cost (USD)</p>
              </div>
              <div className="border rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-primary">{((report.totals?.total_input_tokens || 0) / 1000).toFixed(1)}k</p>
                <p className="text-[11px] text-muted-foreground">Input Tokens</p>
              </div>
              <div className="border rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-primary">{((report.totals?.total_output_tokens || 0) / 1000).toFixed(1)}k</p>
                <p className="text-[11px] text-muted-foreground">Output Tokens</p>
              </div>
            </div>

            {/* By Feature */}
            {report.by_feature?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Cost by Feature</p>
                <table className="data-table w-full">
                  <thead><tr><SortableTableHeader label="Feature" field="feature" sortState={featSort} onSort={hFeat} /><SortableTableHeader label="Calls" field="count" sortState={featSort} onSort={hFeat} className="text-right" /><SortableTableHeader label="Cost (USD)" field="cost" sortState={featSort} onSort={hFeat} className="text-right" /></tr></thead>
                  <tbody>
                    {sortData(report.by_feature, featSort.field, featSort.dir).map(r => (
                      <tr key={r.feature}>
                        <td><Badge variant="outline" className="capitalize">{r.feature.replace(/_/g, ' ')}</Badge></td>
                        <td className="text-right">{r.calls}</td>
                        <td className="text-right font-mono">${r.cost.toFixed(4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* By User */}
            {report.by_user?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Cost by User</p>
                <table className="data-table w-full">
                  <thead><tr><SortableTableHeader label="User" field="user" sortState={userSort} onSort={hUser} /><SortableTableHeader label="Email" field="email" sortState={userSort} onSort={hUser} /><SortableTableHeader label="Calls" field="count" sortState={userSort} onSort={hUser} className="text-right" /><SortableTableHeader label="Cost (USD)" field="cost" sortState={userSort} onSort={hUser} className="text-right" /></tr></thead>
                  <tbody>
                    {sortData(report.by_user, userSort.field, userSort.dir).map(r => (
                      <tr key={r.user_id}>
                        <td className="font-medium">{r.user_id}</td>
                        <td className="text-muted-foreground">{r.email}</td>
                        <td className="text-right">{r.calls}</td>
                        <td className="text-right font-mono">${r.cost.toFixed(4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* By Month */}
            {report.by_month?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Cost by Month</p>
                <table className="data-table w-full">
                  <thead><tr><SortableTableHeader label="Month" field="month" sortState={monthSort} onSort={hMonth} /><SortableTableHeader label="Calls" field="count" sortState={monthSort} onSort={hMonth} className="text-right" /><SortableTableHeader label="Cost (USD)" field="cost" sortState={monthSort} onSort={hMonth} className="text-right" /></tr></thead>
                  <tbody>
                    {sortData(report.by_month, monthSort.field, monthSort.dir).map(r => (
                      <tr key={r.month}>
                        <td className="font-medium">{r.month}</td>
                        <td className="text-right">{r.calls}</td>
                        <td className="text-right font-mono">${r.cost.toFixed(4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {report.totals?.total_calls === 0 && (
              <p className="text-sm text-muted-foreground text-center py-6">No AI usage recorded for {quarter}.</p>
            )}
          </div>
        )}
      </div>

      {/* Recent Usage Log */}
      {usage.data?.length > 0 && (
        <div className="border rounded-xl p-5">
          <h3 className="text-sm font-bold mb-3">Recent AI Calls</h3>
          <table className="data-table w-full">
            <thead><tr><SortableTableHeader label="Time" field="timestamp" sortState={logSort} onSort={hLog} /><SortableTableHeader label="User" field="user_id" sortState={logSort} onSort={hLog} /><SortableTableHeader label="Feature" field="feature" sortState={logSort} onSort={hLog} /><SortableTableHeader label="Detail" field="detail" sortState={logSort} onSort={hLog} /><SortableTableHeader label="Cost" field="cost" sortState={logSort} onSort={hLog} className="text-right" /></tr></thead>
            <tbody>
              {sortData(usage.data, logSort.field, logSort.dir).map((r, i) => (
                <tr key={i}>
                  <td className="text-xs whitespace-nowrap">{new Date(r.timestamp).toLocaleString()}</td>
                  <td className="text-xs">{r.user_id}</td>
                  <td><Badge variant="outline" className="capitalize text-[10px]">{r.feature.replace(/_/g, ' ')}</Badge></td>
                  <td className="text-xs max-w-[300px] truncate">{r.detail}</td>
                  <td className="text-right font-mono text-xs">${r.estimated_cost_usd?.toFixed(6)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
